import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "./useAuth";

// Module-level cache — parallel hook instances share one resolution per user
// per session. Invalidated on sign-out (user change).
type CacheEntry = { isChild: boolean; ts: number };
const cache = new Map<string, CacheEntry>();
const TTL = 5 * 60 * 1000;

function cached(userId: string): boolean | undefined {
  const hit = cache.get(userId);
  if (!hit) return undefined;
  if (Date.now() - hit.ts > TTL) return undefined;
  return hit.isChild;
}

/** Sync cache read — returns `true`/`false` if the user's role is cached,
 *  `null` if the cache is cold. For non-React callers (e.g. captureProcessor)
 *  that need a fast, zero-query guard without mounting a hook. */
export function getCachedIsChildRole(userId: string): boolean | null {
  const hit = cache.get(userId);
  if (!hit) return null;
  if (Date.now() - hit.ts > TTL) return null;
  return hit.isChild;
}

/** Pre-warm the cache from a caller that already resolved the role
 *  (e.g. App.tsx runs the same query at app boot for routing). Avoids a
 *  second roundtrip — and more importantly avoids the cold-cache race when
 *  MemoryMetadataScreen mounts and the user taps Save before the hook's
 *  async resolution lands. */
export function seedIsChildRoleCache(userId: string, isChild: boolean): void {
  cache.set(userId, { isChild, ts: Date.now() });
}

async function resolveIsChildRole(userId: string): Promise<boolean> {
  const { data } = await supabase
    .from("space_members")
    .select("role")
    .eq("user_id", userId)
    .eq("status", "active")
    .limit(1)
    .maybeSingle();
  return (data as { role: string } | null)?.role === "child";
}

/** Returns true when the signed-in user's active space_members row has role='child'.
 *  Mirrors the detection in App.tsx; used by capture UI to hide surfaces (collection
 *  picker) that the RLS policies don't grant children write access to. */
export function useIsChildRole(): { isChild: boolean; loading: boolean } {
  const { user } = useAuth();
  const [isChild, setIsChild] = useState<boolean>(() =>
    user ? cached(user.id) ?? false : false,
  );
  const [loading, setLoading] = useState<boolean>(() =>
    !!user && cached(user.id) === undefined,
  );

  useEffect(() => {
    if (!user) {
      setIsChild(false);
      setLoading(false);
      return;
    }

    const hit = cached(user.id);
    if (hit !== undefined) {
      setIsChild(hit);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    resolveIsChildRole(user.id).then((resolved) => {
      if (cancelled) return;
      cache.set(user.id, { isChild: resolved, ts: Date.now() });
      setIsChild(resolved);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [user]);

  return { isChild, loading };
}
